import { PlusOutlined } from '@ant-design/icons';
import { Pagination, Skeleton, Tabs, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import range from 'lodash/range';
import { useCallback, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import orderApi from '../../apis/order';
import { useHasMounted } from '../../hooks';
import { useDebounceValue } from '../../hooks/useDebouncedValue';
import { IDesign, ISize, ProductType } from '../../interfaces/Order';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';
import { default as CustomInput } from '../Form/FormItem/Input/Input';
import BaseModal from '../Modal/BaseModal/BaseModal';

const START_PAGE = 1;
const PER_PAGE = 20;

export interface SearchDesignProps {
  visible: boolean;
  previewImage?: string;
  previewTitle?: string;
  sizes: ISize[];
  onSelect?: (design: IDesign) => void;
  onCancel?: () => void;
  product?: ProductType;
}

const SearchDesignModal = (props: SearchDesignProps) => {
  const { visible, onSelect, onCancel, product } = props;
  const [sku, setSku] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [searchTerms, setSearchTerms] = useState<string>('');
  const debounceSearchTerms = useDebounceValue(searchTerms, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const [designLink, setDesignLink] = useState<string>();
  const mounted = useHasMounted();
  const [itemsPagination, setItemsPagination] = useState<
    PaginationParamsResponse<IDesign>
  >({
    items: [],
    page: START_PAGE,
    perPage: PER_PAGE,
    total: 0,
    pageCount: 0,
  });
  const [actionLoading, setActionLoading] = useState(false);

  const getDesign = useCallback(async () => {
    try {
      setLoading(true);
      const res = await orderApi.getDesigns(
        {
          page: currentPage,
          perPage: PER_PAGE,
        },
        { sku: debounceSearchTerms || '', product: product ?? '' }
      );

      setItemsPagination(res);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [currentPage, debounceSearchTerms, product]);

  useEffect(() => {
    getDesign();
  }, [debounceSearchTerms, getDesign]);

  useEffect(() => {
    if (currentPage > 1) {
      getDesign();
    }
  }, [currentPage, getDesign]);

  useEffect(() => {
    if (!mounted()) return;

    activeTab === 'search' && getDesign();
  }, [activeTab, mounted, getDesign]);

  const handleChange: UploadProps['onChange'] = (file: any) => {
    setFileList(
      file?.fileList.length
        ? [
            {
              ...file.fileList[0],
              status:
                file.fileList[0].status === 'error'
                  ? 'done'
                  : file.fileList[0].status,
            },
          ]
        : file?.fileList
    );
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const handleSelect = async () => {
    if (activeTab === 'search') {
      if (selectedIndex === undefined) return;

      onSelect?.(itemsPagination.items?.[selectedIndex]);
      onCancel?.();
      return;
    }

    if (
      activeTab === 'upload' &&
      fileList.length === 0 &&
      (!designLink || designLink.length === 0)
    ) {
      return;
    }

    const formData = new FormData();
    if (fileList.length > 0) {
      formData.append('design', fileList[0]?.originFileObj || '');
    } else {
      formData.append('designUrl', designLink || '');
    }
    formData.append('sku', sku);
    formData.append('product', product?.toString() ?? '');

    try {
      setActionLoading(true);
      const res = await orderApi.createDesign(formData);
      setFileList([]);
      setSku('');
      setActiveTab('search');
      onSelect?.(res);
    } catch (error) {
      // navigate(-1);
      console.error(error);
    } finally {
      setActionLoading(false);
      onCancel?.();
    }
  };

  return (
    <StyledModal
      visibleModal={visible}
      title="Designs"
      // footer={null}
      onPrimaryBtnClick={handleSelect}
      onSecondaryBtnClick={() => {
        onCancel?.();
      }}
      primaryBtn="Select"
      secondaryBtn="Cancel"
      confirmLoading={actionLoading}
      destroyOnClose={true}
      titleAlign="left"
      headerPadding="0"
    >
      <StyledTabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={[
          {
            key: 'search',
            label: 'Search Design',
            children: (
              <div id="infinite-scroll-container">
                <CustomInput
                  placeholder="Search by SKU"
                  value={searchTerms}
                  onChange={(event) => setSearchTerms(event.target.value)}
                  className="m__b--20"
                />
                {loading ? (
                  <ListDesign>
                    {range(5).map((n) => (
                      <DesignItemContainer $selected={false} key={n}>
                        <SkeletonImageWrapper>
                          <Skeleton.Image active />
                        </SkeletonImageWrapper>
                      </DesignItemContainer>
                    ))}
                  </ListDesign>
                ) : (
                  <ListDesign>
                    {itemsPagination.items?.map((d, idx) => (
                      <DesignItemContainer
                        key={idx}
                        $selected={idx === selectedIndex}
                        onClick={() => setSelectedIndex(idx)}
                      >
                        {d.isGeneratingThumbnail ? (
                          <SkeletonImageWrapper>
                            <Skeleton.Image active />
                          </SkeletonImageWrapper>
                        ) : (
                          <DesignItem src={d.thumbnailUrl ?? d.designUrl} />
                        )}
                      </DesignItemContainer>
                    ))}
                  </ListDesign>
                )}

                <Pagination
                  className="m__t--20"
                  total={itemsPagination.total}
                  // defaultPageSize={PER_PAGE}
                  showSizeChanger={false}
                  defaultCurrent={1}
                  onChange={(page) => {
                    setCurrentPage(page);
                  }}
                />
                {/* <div style={{ height: 300 }}>
                  <InfiniteScroll
                    dataLength={designItems.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    loader={<h4 style={{ textAlign: 'center' }}>Loading.....</h4>}
                    scrollableTarget="infinite-scroll-container"
                    // scrollThreshold={0.9}
                    initialScrollY={0}
                  >
                    <ListDesign>
                      {designItems?.map((d, idx) => (
                        <DesignItemContainer
                          key={idx}
                          $selected={idx === selectedIndex}
                          onClick={() => setSelectedIndex(idx)}
                        >
                          <DesignItem src={d.designUrl} />
                        </DesignItemContainer>
                      ))}
                    </ListDesign>
                  </InfiniteScroll>
                </div> */}
              </div>
            ),
          },
          {
            key: 'upload',
            label: 'Upload Design',
            children: (
              <>
                <Label>SKU</Label>
                <CustomInput
                  className="m__b--20"
                  value={sku}
                  onChange={(e) => {
                    setSku(e.target.value);
                    // setError(!Boolean(e.target.value));
                  }}
                />
                {/* {error && <p>SKU is missing</p>} */}
                <Label>Design Link</Label>
                <CustomInput
                  className="m__b--20"
                  value={designLink}
                  placeholder="Paste design link here..."
                  onChange={(e) => {
                    setDesignLink(e.target.value);
                    // setError(!Boolean(e.target.value));
                  }}
                  disabled={fileList.length > 0}
                />
                <p className="m__b--20">Or upload a design file</p>
                <StyledUpload
                  listType="picture-card"
                  fileList={fileList}
                  // onPreview={handlePreview}
                  onChange={handleChange}
                  multiple={false}
                  disabled={!!designLink && designLink.length > 0}
                  accept="image/*"
                >
                  {fileList.length >= 1 ? null : uploadButton}
                </StyledUpload>
              </>
            ),
          },
        ]}
      />
    </StyledModal>
  );
};

export default SearchDesignModal;

export const StyledModal = styled(BaseModal)`
  &.ant-modal {
    width: 840px !important;
  }

  .ant-modal-footer {
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-content-holder {
    max-height: 500px;
    overflow-y: auto;
  }

  .ant-tabs-tab {
    font-weight: 500;
  }
`;

const StyledUpload = styled(Upload)`
  .ant-upload-list-item-container {
    width: 160px;
    height: 160px;
  }
`;

const Label = styled.p`
  text-align: left;
  margin-bottom: 5px;
`;

const ListDesign = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  min-height: 60px;
  max-height: 430px;
  overflow: auto;
`;

const DesignItemContainer = styled.div<{ $selected: boolean }>`
  width: 140px;
  height: 140px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  display: grid;
  align-items: center;
  justify-content: center;

  ${({ $selected }) =>
    $selected &&
    `
    border-color: #4096ff;
  `}
`;

const DesignItem = styled.img`
  max-width: 92px;
  max-height: 92px;
  object-fit: contain;
`;

export const SkeletonImageWrapper = styled.div`
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: contain;

  .ant-skeleton-image {
    width: 92px !important;
    height: 92px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    object-fit: contain;
  }
`;
